import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;

export type GamePassSocialEventRecord = {
  event_id?: string;
  event_type?: string;
  user_id?: string;
  occurred_at?: string | number;
  payload?: Record<string, unknown>;
  [key: string]: unknown;
};

export type GamePassUserSocialEventsResponse = {
  success?: boolean;
  events?: GamePassSocialEventRecord[];
  data?:
    | GamePassSocialEventRecord[]
    | { events?: GamePassSocialEventRecord[]; total?: number };
  total?: number;
  page?: number;
  limit?: number;
};

const buildSocialEventsUrl = (userId: string): string =>
  API_ROUTES.GET_GAME_PASS_USER_SOCIAL_EVENTS.replace("{user_id}", userId);

const fetchGamePassUserSocialEvents = async (
  userId: string,
  eventType?: string,
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
): Promise<GamePassUserSocialEventsResponse> => {
  const response =
    await getPrivateInstance().get<GamePassUserSocialEventsResponse>(
      buildSocialEventsUrl(userId),
      {
        params: {
          page,
          limit,
          ...(eventType ? { event_type: eventType } : {}),
        },
      },
    );
  return response.data;
};

type UseGetGamePassUserSocialEventsOptions = {
  enabled?: boolean;
  eventType?: string;
  page?: number;
  limit?: number;
};

/**
 * `GET /gamepass/v2/admin/users/{user_id}/social-events` — referral / follow /
 * rebet-style history rows for the Monthly Challenges support surface.
 */
export const useGetGamePassUserSocialEvents = (
  userId?: string,
  options?: UseGetGamePassUserSocialEventsOptions,
): UseQueryResult<GamePassUserSocialEventsResponse, Error> => {
  const page = options?.page ?? DEFAULT_PAGE;
  const limit = options?.limit ?? DEFAULT_LIMIT;
  const eventType = options?.eventType ?? "";

  return useQuery<GamePassUserSocialEventsResponse, Error>({
    queryKey: [
      "game-pass",
      "users",
      "social-events",
      userId ?? "",
      eventType,
      page,
      limit,
    ],
    queryFn: () =>
      fetchGamePassUserSocialEvents(userId!, eventType || undefined, page, limit),
    enabled: Boolean(userId) && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });
};

export { buildSocialEventsUrl, fetchGamePassUserSocialEvents };
